import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service';

/**
 * Gestionnaire d'erreurs global de l'application.
 *
 * <p>Attrape les erreurs JavaScript non gérées (exceptions runtime, promesses
 * rejetées, erreurs dans les souscriptions RxJS) pour :</p>
 * <ul>
 *   <li>les journaliser de façon homogène dans la console ;</li>
 *   <li>informer l'utilisateur via un toast générique plutôt qu'un échec
 *       silencieux.</li>
 * </ul>
 *
 * <p>Les {@link HttpErrorResponse} sont <b>ignorées ici</b> : elles sont déjà
 * traitées par {@code errorInterceptor} (toasts + redirections), inutile de
 * doubler le message.</p>
 *
 * <p>{@link NotificationService} est résolu paresseusement via l'{@link Injector}
 * pour éviter les problèmes d'ordre d'initialisation (l'ErrorHandler est créé
 * très tôt au démarrage).</p>
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);

  handleError(error: unknown): void {
    // Angular encapsule parfois l'erreur d'origine dans `.rejection`.
    const cause =
      error && typeof error === 'object' && 'rejection' in error
        ? (error as { rejection: unknown }).rejection
        : error;

    // Déjà géré par errorInterceptor → on ne notifie pas deux fois.
    if (cause instanceof HttpErrorResponse) {
      return;
    }

    console.error('[Dinki] Erreur non gérée :', cause);

    try {
      this.injector
        .get(NotificationService)
        .error('Une erreur inattendue est survenue.');
    } catch {
      // NotificationService indisponible (erreur très précoce au boot) — on
      // se contente du log console déjà émis.
    }
  }
}
